import {
  useFormikContext,
  FormikErrors,
  FormikProps,
  FormikValues,
} from "formik";
import { useCallback } from "react";

/**
 * Enhanced FormikProps with typed methods for better type safety
 */
export type TypedFormikProps<Values extends FormikValues> = Omit<
  FormikProps<Values>,
  "setFieldValue" | "setFieldTouched" | "setFieldError" | "validateField"
> & {
  setFieldValue: <T extends keyof Values>(
    field: T,
    value: Values[T],
    shouldValidate?: boolean
  ) => Promise<void | FormikErrors<Values>>;
  setFieldTouched: (
    field: keyof Values,
    isTouched?: boolean,
    shouldValidate?: boolean
  ) => Promise<void | FormikErrors<Values>>;
  setFieldError: (field: keyof Values, message: string | undefined) => void;
  validateField: (
    field: keyof Values
  ) => Promise<void> | Promise<string | undefined>;
};

/**
 * Type-safe wrapper around useFormikContext that provides enhanced type safety
 * for field operations with strict keyof Values typing.
 */
export function useTypedFormikContext<
  Values extends FormikValues
>(): TypedFormikProps<Values> {
  const formikContext = useFormikContext<Values>();
  const {
    setFieldValue: formikSetFieldValue,
    setFieldTouched: formikSetFieldTouched,
    setFieldError: formikSetFieldError,
    validateField: formikValidateField,
  } = formikContext;

  // Wrappers are memoized so they stay as stable as Formik's own setters and
  // can be listed in effect dependency arrays without re-running every render
  const setFieldValue = useCallback(
    <T extends keyof Values>(
      field: T,
      value: Values[T],
      shouldValidate?: boolean
    ): Promise<void | FormikErrors<Values>> =>
      formikSetFieldValue(field as string, value, shouldValidate),
    [formikSetFieldValue]
  );

  const setFieldTouched = useCallback(
    (
      field: keyof Values,
      isTouched?: boolean,
      shouldValidate?: boolean
    ): Promise<void | FormikErrors<Values>> =>
      formikSetFieldTouched(field as string, isTouched, shouldValidate),
    [formikSetFieldTouched]
  );

  const setFieldError = useCallback(
    (field: keyof Values, message: string | undefined): void =>
      formikSetFieldError(field as string, message),
    [formikSetFieldError]
  );

  const validateField = useCallback(
    (field: keyof Values): Promise<void> | Promise<string | undefined> =>
      formikValidateField(field as string) as
        | Promise<void>
        | Promise<string | undefined>,
    [formikValidateField]
  );

  // Return the context with enhanced type-safe methods
  return {
    ...formikContext,
    setFieldValue,
    setFieldTouched,
    setFieldError,
    validateField,
  };
}
